import type { EmployeeSortProperty } from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import "../../components/avatar/avatar";
import "../../elements/entity-filters";
import type { EntityFiltersEl } from "../../elements/entity-filters";
import "../../elements/popover";
import "../../elements/scroller";
import { app } from "../../init";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";

const sortPropLabels: Record<EmployeeSortProperty, string> = {
    firstName: "Vorname",
    lastName: "Nachname",
    staffNumber: "Personalnummer",
    birthday: "Geburtstag",
    birthdayDate: "Geburtstag (Datum)",
    email: "Email",
    contractEnd: "Vertragsende",
    employedDays: "Betriebszugehörigkeit",
};

@customElement("ptc-employees-filter")
export class EmployeesFilter extends StateMixin(LitElement) {
    @property({ type: Boolean })
    disableSorting = false;

    @property({ type: Boolean })
    hideEmployeeCount: boolean = false;

    @property({ type: Array })
    sortableProps: EmployeeSortProperty[] = [
        "firstName",
        "lastName",
        "staffNumber",
        "birthday",
        "birthdayDate",
        "email",
        "contractEnd",
        "employedDays",
    ];

    @query("ptc-entity-filters")
    private _entityFilters: EntityFiltersEl;

    @query("#sortPropertySelect")
    private _sortPropertySelect: HTMLInputElement;

    updated(changedProperties: Map<PropertyKey, unknown>) {
        // Since we limit the sortable properties, we need to make sure the current sort property is included in the current list.
        if (changedProperties.has("sortableProps")) {
            const currentSortProp = app.employeeFilters.sortProperty;
            if (!this.sortableProps.includes(currentSortProp)) {
                app.updateEmployeeFilters({ sortProperty: "lastName" }); // Last name is the default
            }
        }
    }

    private _filtersChanged() {
        app.updateEmployeeFilters({ filters: this._entityFilters.filters });
        this._fireChange();
    }

    private _toggleSortDirection() {
        if (app.employeeFilters.sortDirection === "ascending") {
            app.updateEmployeeFilters({ sortDirection: "descending" });
        } else {
            app.updateEmployeeFilters({ sortDirection: "ascending" });
        }
        this._fireChange();
    }

    private _sortPropertySelected() {
        app.updateEmployeeFilters({
            sortProperty: this._sortPropertySelect.value as EmployeeSortProperty,
        });
        this._fireChange();
    }

    private _fireChange() {
        this.dispatchEvent(new CustomEvent("change", { detail: { filters: app.state.employeeFilters } }));
    }

    static styles = [shared, css``];

    render() {
        return html`
            <div class="center-aligning spacing horizontal layout">
                <ptc-entity-filters
                    .filters=${app.employeeFilters.filters}
                    @change=${this._filtersChanged}
                    ?hideEmployeeCount=${this.hideEmployeeCount}
                    class="stretch"
                ></ptc-entity-filters>

                ${app.employeeFilters.sortDirection === "ascending"
                    ? html`
                          <button
                              class="skinny transparent"
                              @click=${this._toggleSortDirection}
                              ?hidden=${this.disableSorting}
                              title="Aufsteigend Sortieren (Klicken zum Ändern)"
                          >
                              <ptc-ui-icon name="icon.action.sort-ascending"></ptc-ui-icon>
                          </button>
                      `
                    : html`
                          <button
                              class="skinny transparent"
                              @click=${this._toggleSortDirection}
                              ?hidden=${this.disableSorting}
                              title="Absteigend sortieren (Klicken zum Ändern)"
                          >
                              <ptc-ui-icon name="icon.action.sort-descending"></ptc-ui-icon>
                          </button>
                      `}

                <div class="select-wrapper click rounded" ?hidden=${this.disableSorting}>
                    <select
                        class="semibold small skinny plain transparent"
                        id="sortPropertySelect"
                        .value=${live(app.employeeFilters.sortProperty)}
                        @change=${this._sortPropertySelected}
                    >
                        ${this.sortableProps.map(
                            (prop) => html`<option value=${prop}>${sortPropLabels[prop]}</option>`
                        )}
                    </select>
                    <ptc-ui-icon name="icon.navigation.dropdown"></ptc-ui-icon>
                </div>
            </div>
        `;
    }
}
